// =============================================================================
// AbsenceReasonsManager - Gestione causali di assenza (CRUD)
// =============================================================================
// Elenco delle causali dell'organizzazione con creazione/modifica inline,
// attivazione/disattivazione ed eliminazione.

import { useState } from 'react';
import { Plus, Loader2, Edit2, Trash2, Tag } from 'lucide-react';
import { EmptyState } from '../common';
import { useTranslation } from 'react-i18next';
import {
  useAbsenceReasons,
  useUpdateAbsenceReason,
  useDeleteAbsenceReason,
  AbsenceReason,
} from '../../api/hooks';
import { resolveIcon, hexToRgba } from '../scheduler/utils/exceptionStyle';
import { Toggle } from '../ui/Toggle';
import { AbsenceReasonForm } from './AbsenceReasonForm';

export function AbsenceReasonsManager() {
  const { t } = useTranslation('settings');
  const { data: reasons = [], isLoading } = useAbsenceReasons(false);
  const updateReason = useUpdateAbsenceReason();
  const deleteReason = useDeleteAbsenceReason();

  const [editing, setEditing] = useState<AbsenceReason | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const closeForm = () => {
    setEditing(null);
    setIsCreating(false);
  };

  const handleToggleActive = (r: AbsenceReason) => {
    updateReason.mutate({ id: r.id, isActive: !r.isActive });
  };

  const handleDelete = async (r: AbsenceReason) => {
    if (!window.confirm(t('absenceReasons.deleteConfirm', { name: r.name }))) return;
    try {
      await deleteReason.mutateAsync(r.id);
    } catch (e) {
      console.error('Error deleting absence reason:', e);
    }
  };

  if (isCreating || editing) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <AbsenceReasonForm reason={editing} onClose={closeForm} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <span className="text-sm font-medium text-gray-700">
          {reasons.length} {t('absenceReasons.count')}
        </span>
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('absenceReasons.add')}
        </button>
      </div>

      {isLoading ? (
        <div className="py-10 text-center">
          <Loader2 className="w-5 h-5 text-emerald-600 animate-spin mx-auto" />
        </div>
      ) : reasons.length === 0 ? (
        <EmptyState icon={Tag} title={t('absenceReasons.empty')} size="sm" className="py-10" />
      ) : (
        <ul className="divide-y divide-gray-100">
          {reasons.map((r) => {
            const Icon = resolveIcon(r.icon);
            return (
              <li key={r.id} className="flex items-center gap-3 px-4 py-2.5">
                <span
                  className="p-2 rounded-lg flex-shrink-0"
                  style={{ backgroundColor: hexToRgba(r.color, 0.12), color: r.color }}
                >
                  <Icon className="w-4 h-4" />
                </span>
                <span className={`flex-1 min-w-0 font-medium truncate ${r.isActive ? 'text-gray-900' : 'text-gray-400'}`}>
                  {r.name}
                  {!r.isActive && (
                    <span className="ml-2 text-xs font-normal text-gray-400">
                      ({t('absenceReasons.inactive')})
                    </span>
                  )}
                </span>

                <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                  <Toggle checked={r.isActive} onChange={() => handleToggleActive(r)} />
                </label>
                <button
                  onClick={() => setEditing(r)}
                  className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                  title={t('common:buttons.edit')}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(r)}
                  disabled={deleteReason.isPending}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  title={t('common:buttons.delete')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
